/*
 * /referral (admin list page)
 */
import {
	Button,
	Card,
	CardContent,
	Container,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Page,
	PageHeader,
	Placeholder,
	toast,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { Building2, Pencil, Plus, Trash2 } from "lucide-react"
import { useState } from "react"
import { trpc } from "../../trpc"
import type { Referral } from "@emr/db/client"

export const Route = createFileRoute("/referral/")({
	component: ReferralListPage,
})

type FormState = {
	companyName: string
	contactName: string
	address: string
	phoneNumber: string
	faxNumber: string
	email: string
}
const EMPTY: FormState = { companyName: "", contactName: "", address: "", phoneNumber: "", faxNumber: "", email: "" }

function ReferralListPage() {
	const utils = trpc.useUtils()
	const { data: referrals, isLoading } = trpc.referral.getAllByFilter.useQuery({ where: { active: true } })

	const [dialogOpen, setDialogOpen] = useState(false)
	const [editing, setEditing] = useState<Referral | null>(null)
	const [form, setForm] = useState<FormState>(EMPTY)

	const create = trpc.referral.create.useMutation({
		onSuccess: () => { utils.referral.getAllByFilter.invalidate(); closeDialog(); toast.success("Referral added") },
		onError: (e) => toast.error(e.message),
	})
	const update = trpc.referral.update.useMutation({
		onSuccess: () => { utils.referral.getAllByFilter.invalidate(); closeDialog(); toast.success("Referral updated") },
		onError: (e) => toast.error(e.message),
	})
	const remove = trpc.referral.update.useMutation({
		onSuccess: () => { utils.referral.getAllByFilter.invalidate(); toast.success("Referral removed") },
		onError: (e) => toast.error(e.message),
	})

	function openNew() { setEditing(null); setForm(EMPTY); setDialogOpen(true) }
	function openEdit(r: Referral) {
		setEditing(r)
		setForm({
			companyName: r.companyName,
			contactName: r.contactName ?? "",
			address: r.address ?? "",
			phoneNumber: r.phoneNumber ?? "",
			faxNumber: r.faxNumber ?? "",
			email: r.email ?? "",
		})
		setDialogOpen(true)
	}
	function closeDialog() { setDialogOpen(false); setEditing(null); setForm(EMPTY) }

	function handleSave() {
		if (!form.companyName.trim()) return
		const payload = {
			companyName: form.companyName.trim(),
			contactName: form.contactName.trim() || null,
			address: form.address.trim() || null,
			phoneNumber: form.phoneNumber.trim() || null,
			faxNumber: form.faxNumber.trim() || null,
			email: form.email.trim() || null,
		}
		if (editing) {
			update.mutate({ id: editing.id, ...payload } as any)
		} else {
			create.mutate(payload as any)
		}
	}

	const isSaving = create.isPending || update.isPending
	const f = (key: keyof FormState) => ({
		value: form[key],
		onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, [key]: e.target.value })),
	})

	return (
		<Page centerVertical={false}>
			<Container width="xl" padding="lg" gap="lg">
				<PageHeader
					title="Referrals"
					right={{
						Component: (
							<Button onClick={openNew}>
								<Plus className="w-4 h-4 mr-1" />
								Add Referral
							</Button>
						),
					}}
				/>

				{isLoading ? (
					<Placeholder title="Loading…" />
				) : !referrals || referrals.length === 0 ? (
					<Placeholder title="No referrals yet" />
				) : (
					<div className="flex flex-col gap-3">
						{referrals.map((r) => (
							<Card key={r.id}>
								<CardContent className="flex items-center justify-between py-3">
									<div className="flex items-center gap-3">
										<Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
										<div>
											<p className="font-medium">{r.companyName}</p>
											<div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-sm text-muted-foreground">
												{r.contactName && <span>{r.contactName}</span>}
												{r.faxNumber && <span>Fax: {r.faxNumber}</span>}
												{r.phoneNumber && <span>Phone: {r.phoneNumber}</span>}
												{r.email && <span>{r.email}</span>}
											</div>
											{r.address && <p className="text-xs text-muted-foreground mt-0.5">{r.address}</p>}
										</div>
									</div>
									<div className="flex items-center gap-1 shrink-0">
										<Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)}>
											<Pencil className="w-4 h-4" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8 text-destructive hover:text-destructive"
											onClick={() => remove.mutate({ id: r.id, active: false } as any)}
										>
											<Trash2 className="w-4 h-4" />
										</Button>
									</div>
								</CardContent>
							</Card>
						))}
					</div>
				)}
			</Container>

			<Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) closeDialog() }}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>{editing ? "Edit Referral" : "Add Referral"}</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-3 py-2">
						<Field label="Company Name" required>
							<Input placeholder="e.g. Ottawa Heart Institute" {...f("companyName")} />
						</Field>
						<Field label="Contact Name">
							<Input placeholder="e.g. Dr. Smith" {...f("contactName")} />
						</Field>
						<Field label="Address">
							<Input placeholder="123 Main St, City, Province" {...f("address")} />
						</Field>
						<div className="grid grid-cols-2 gap-3">
							<Field label="Phone">
								<Input placeholder="613-555-0100" {...f("phoneNumber")} />
							</Field>
							<Field label="Fax">
								<Input placeholder="613-555-0101" {...f("faxNumber")} />
							</Field>
						</div>
						<Field label="Email">
							<Input placeholder="referrals@clinic.com" {...f("email")} />
						</Field>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={closeDialog}>Cancel</Button>
						<Button onClick={handleSave} disabled={!form.companyName.trim() || isSaving}>
							{isSaving ? "Saving…" : "Save"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</Page>
	)
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
	return (
		<div className="flex flex-col gap-1.5">
			<label className="text-sm font-medium">
				{label} {required && <span className="text-destructive">*</span>}
			</label>
			{children}
		</div>
	)
}
